import { databases } from '@/lib/appwrite';
import { Query } from 'appwrite';

const DATABASE_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const COLLECTION_ID = import.meta.env.VITE_CERTIFICATE_REQUESTS_COLLECTION_ID;

// Tipo exacto según tus atributos
export type CertificateRequest = {
    $id: string;
    $createdAt: string;
    $updatedAt: string;
    userId: string;
    reason: string;
    requestDate: string; // datetime
    status: string;
    approvedAt: string; // datetime
    commentsRrhh: string;
    commentsManager: string;
    commentsSupervisor: string;
    certificateFileId: string;
    type: string;
    statusByRrhh: string;
    statusByManager: string;
    statusBySupervisor: string;
    idApprovedByRrhh: string;
    idApprovedByManager: string;
    idApprovedBySupervisor: string;

};

// Obtener todas las solicitudes
export const getAllCertificateRequests = async (): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.orderDesc('requestDate'),
                Query.limit(100)
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes por userId
export const getCertificateRequestsByUser = async (userId: string): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    if (!userId) {
        return { success: false, error: 'userId es requerido' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.equal('userId', userId),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes del usuario:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener una solicitud por ID
export const getCertificateRequestById = async (requestId: string): Promise<{
    success: boolean;
    data?: CertificateRequest;
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    if (!requestId) {
        return { success: false, error: 'requestId es requerido' };
    }

    try {
        const response = await databases.getDocument(
            DATABASE_ID,
            COLLECTION_ID,
            requestId
        );

        return {
            success: true,
            data: response as unknown as CertificateRequest
        };
    } catch (error: any) {
        console.error('Error al obtener la solicitud:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes por tipo de certificado
export const getCertificateRequestsByType = async (type: string): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.equal('type', type),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes por tipo:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes por estado general
export const getCertificateRequestsByStatus = async (status: string): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.equal('status', status),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes por estado:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes por estado de RRHH
export const getCertificateRequestsByRrhhStatus = async (statusByRrhh: string): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.equal('statusByRrhh', statusByRrhh),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes por estado RRHH:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes por estado del Manager
export const getCertificateRequestsByManagerStatus = async (statusByManager: string): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.equal('statusByManager', statusByManager),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes por estado Manager:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes aprobadas (que tienen approvedAt)
export const getApprovedCertificateRequests = async (): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.isNotNull('approvedAt'),
                Query.orderDesc('approvedAt')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes aprobadas:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes con certificado generado (que tienen certificateFileId)
export const getCertificateRequestsWithFile = async (): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.isNotNull('certificateFileId'),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes con archivo:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes pendientes (sin approvedAt)
export const getPendingCertificateRequests = async (): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            [
                Query.isNull('approvedAt'),
                Query.orderDesc('requestDate')
            ]
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes pendientes:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};

// Obtener solicitudes con filtros personalizados
export const getCertificateRequestsWithFilters = async (queries: string[]): Promise<{
    success: boolean;
    data?: CertificateRequest[];
    error?: string;
}> => {
    if (!DATABASE_ID || !COLLECTION_ID) {
        return { success: false, error: 'Faltan configuraciones de base de datos' };
    }

    try {
        const response = await databases.listDocuments(
            DATABASE_ID,
            COLLECTION_ID,
            queries
        );

        return {
            success: true,
            data: response.documents as unknown as CertificateRequest[]
        };
    } catch (error: any) {
        console.error('Error al obtener solicitudes con filtros:', error);
        return {
            success: false,
            error: error?.message || 'Error desconocido'
        };
    }
};